<script lang="ts" setup>
import { computed, type PropType } from "vue";

import { ColorVariant, Size } from "@/abc-dsk/options";
import {
  color as colorProp,
  colorVariant as colorVariantProp,
  gradient as gradientProp
} from "@/abc-dsk/props";

import { CLASS_NAME_COMPONENT } from "./common";

type SizeType = Size.Md | Size.Lg;
const props = defineProps({
  color: colorProp,
  colorVariant: colorVariantProp,
  gradient: gradientProp,
  size: {
    type: String as PropType<SizeType>,
    default: Size.Md
  },
  screenReaderLabel: {
    type: String,
    required: true
  },
  max: {
    type: Number,
    required: true
  },
  value: {
    type: Number,
    default: 0
  },
  duration: {
    type: Number,
    default: 500
  }
});

const componentClasses = computed(() => [
  CLASS_NAME_COMPONENT,
  `${CLASS_NAME_COMPONENT}--${props.color}`,
  `${CLASS_NAME_COMPONENT}--${props.size}`
]);

const progressPercentage = computed(() => (props.value / props.max) * 100);

const transition = computed(() => `width ${props.duration}ms ease-in-out`);

const backgroundStyle = computed(() => {
  if (props.gradient) {
    return `var(--${props.gradient}-linear-gradient)`;
  }

  return `rgb(var(--${props.color}-${
    props.colorVariant ? props.colorVariant : ColorVariant.Base
  }-color))`;
});
</script>

<template>
  <div
    :class="componentClasses"
    role="progressbar"
    :aria-valuemin="0"
    :aria-valuemax="max"
    :aria-valuenow="value"
    :aria-valuetext="`${value} / ${max}`"
    :aria-label="screenReaderLabel"
  >
    <span
      :class="`${CLASS_NAME_COMPONENT}__value`"
      :style="{ width: `${progressPercentage}%`, background: backgroundStyle }"
    >
      <slot />
    </span>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.tg-progress {
  position: relative;
  width: 100%;
  height: 10px;
  overflow: hidden;
  border-radius: 8px;
  background: rgb(@theme-colors[@grey][25]);

  &--md {
    --tg-progress-value-border-radius: 5px;
    height: 10px;
    border-radius: 5px;
  }

  &--lg {
    --tg-progress-value-border-radius: 10px;
    height: 20px;
    border-radius: 10px;
  }

  &__value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    height: 100%;
    transition: v-bind("transition");
    border-radius: var(--tg-progress-value-border-radius, 5px);
  }
}
</style>
